import { INFO_NOT_AVAILABLE_NA } from '@backoffice-frontend/common'
import type { Maybe, VehicleState } from '@backoffice-frontend/graphql'
import {
  ClimateControlState,
  InteractionReason,
  SdsProvider,
  VehicleInteractionError,
  VehicleInteractionType,
  VehicleNonOperationalReasonType,
  VehicleOperationalState,
  VehicleStateChangeReason,
  VehicleType,
} from '@backoffice-frontend/graphql'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import type { VehicleCommonAreaId } from '../VehicleCommonAreaId'

export const useVehicleStateTranslation = () => {
  const { t } = useTranslation()

  return (vehicleState?: Maybe<VehicleState> | undefined): string => {
    if (!vehicleState) return INFO_NOT_AVAILABLE_NA

    const options = {
      ACTIVE: t('common_state_active'),
      ARCHIVED: t('common_state_archived'),
      DRAFT: t('common_state_draft'),
      INACTIVE: t('common_state_inactive'),
    }

    return options[vehicleState] ?? vehicleState
  }
}

export const useVehicleTypeTranslation = () => {
  const { t } = useTranslation()

  return (type: VehicleType): string => {
    const translations: Record<VehicleType, string> = {
      [VehicleType.ECrafter]: t('vehicleCommon_vehicleType_eCrafter'),
      [VehicleType.IdBuzzAd]: t('vehicleCommon_vehicleType_idBuzzAd'),
      [VehicleType.IdBuzzPrep]: t('vehicleCommon_vehicleType_idBuzzPrep'),
      [VehicleType.MachE]: t('vehicleCommon_vehicleType_machE'),
      [VehicleType.Mover]: t('vehicleCommon_vehicleType_mover'),
      [VehicleType.Pluto]: t('vehicleCommon_vehicleType_pluto'),
      [VehicleType.T6]: t('vehicleCommon_vehicleType_t6'),
    }
    return translations[type] ?? type
  }
}

export const useSdsProviderTranslation = () => {
  const { t } = useTranslation()

  return (provider: SdsProvider): string => {
    const translations: Record<SdsProvider, string> = {
      [SdsProvider.Mobileye]: t('vehicleCommon_sdsProvider_mobileye'),
      [SdsProvider.None]: t('vehicleCommon_sdsProvider_none'),
      [SdsProvider.Simulated]: t('vehicleCommon_sdsProvider_simulated'),
      [SdsProvider.Wayve]: t('vehicleCommon_sdsProvider_wayve'),
    }
    return translations[provider] ?? provider
  }
}

export const useVehicleOperationalStateTranslation = () => {
  const { t } = useTranslation()

  return (operationalState: VehicleOperationalState): string => {
    const options = {
      [VehicleOperationalState.Operational]: t(
        'common_operationalState_operational_label',
      ),
      [VehicleOperationalState.NonOperational]: t(
        'common_operationalState_nonOperational_label',
      ),
    }

    return options[operationalState] ?? operationalState
  }
}

export const getPreconditionButtonLabel = (
  t: TFunction<typeof VehicleCommonAreaId>,
  key: ClimateControlState | null | undefined,
) => {
  if (!key) {
    return t('vehicleCommon_climateControlState_unknown')
  }
  return {
    [ClimateControlState.Off]: t('vehicleCommon_climateControlState_off'),
    [ClimateControlState.Cooling]: t(
      'vehicleCommon_climateControlState_cooling',
    ),
    [ClimateControlState.Heating]: t(
      'vehicleCommon_climateControlState_heating',
    ),
  }[key]
}

export const useVehicleInteractionErrorMessage = () => {
  const { t } = useTranslation()

  return (
    vehicleLabel: number | undefined | null,
    errorReason: string,
    interactionType: VehicleInteractionType | undefined,
  ) => {
    if (!interactionType) {
      return t('vehicleCommon_vehicleInteraction_error_unknown')
    }
    return {
      [VehicleInteractionType.Open]: t(
        'vehicleCommon_vehicleInteraction_error_open',
        {
          vehicleLabel,
          errorReason,
        },
      ),
      [VehicleInteractionType.Close]: t(
        'vehicleCommon_vehicleInteraction_error_close',
        { vehicleLabel, errorReason },
      ),
    }[interactionType]
  }
}

export const getVehicleInteractionSuccessMessage = (
  t: TFunction<typeof VehicleCommonAreaId>,
  key: VehicleInteractionType | undefined,
  params: Record<string, string | number>,
) => {
  if (!key) {
    return t('vehicleCommon_vehicleInteraction_success_unknown')
  }
  return {
    [VehicleInteractionType.Open]: t(
      'vehicleCommon_vehicleInteraction_success_open',
      params,
    ),
    [VehicleInteractionType.Close]: t(
      'vehicleCommon_vehicleInteraction_success_close',
      params,
    ),
  }[key]
}

export const getVehicleInteractionButtonLabel = (
  t: TFunction<typeof VehicleCommonAreaId>,
  key: VehicleInteractionType | undefined,
) => {
  if (!key) {
    return t('vehicleCommon_vehicleInteraction_button_unknown')
  }
  return {
    [VehicleInteractionType.Open]: t(
      'vehicleCommon_vehicleInteraction_button_open',
    ),
    [VehicleInteractionType.Close]: t(
      'vehicleCommon_vehicleInteraction_button_close',
    ),
  }[key]
}

export const getVehicleInteractionReasonLabel = (
  t: TFunction<typeof VehicleCommonAreaId>,
  key: InteractionReason | null,
): string => {
  if (!key) {
    return t('vehicleCommon_vehicleInteraction_reason_unknown')
  }
  return {
    [InteractionReason.AppProblems]: t(
      'vehicleCommon_vehicleInteraction_reason_appProblems',
    ),
    [InteractionReason.Charging]: t(
      'vehicleCommon_vehicleInteraction_reason_charging',
    ),
    [InteractionReason.Cleaning]: t(
      'vehicleCommon_vehicleInteraction_reason_cleaning',
    ),
    [InteractionReason.ForgotSomething]: t(
      'vehicleCommon_vehicleInteraction_reason_forgotSomething',
    ),
    [InteractionReason.InsideCleaning]: t(
      'vehicleCommon_vehicleInteraction_reason_insideCleaning',
    ),
    [InteractionReason.Inspection]: t(
      'vehicleCommon_vehicleInteraction_reason_inspection',
    ),
    [InteractionReason.Maintenance]: t(
      'vehicleCommon_vehicleInteraction_reason_maintenance',
    ),
    [InteractionReason.Other]: t(
      'vehicleCommon_vehicleInteraction_reason_other',
    ),
    [InteractionReason.OutsideCleaning]: t(
      'vehicleCommon_vehicleInteraction_reason_outsideCleaning',
    ),
    [InteractionReason.Reparking]: t(
      'vehicleCommon_vehicleInteraction_reason_reparking',
    ),
    [InteractionReason.ShiftAssignment]: t(
      'vehicleCommon_vehicleInteraction_reason_shiftAssignment',
    ),
    [InteractionReason.TelemetryProblems]: t(
      'vehicleCommon_vehicleInteraction_reason_telemetryProblems',
    ),
    [InteractionReason.Transfer]: t(
      'vehicleCommon_vehicleInteraction_reason_transfer',
    ),
    [InteractionReason.FrontSeatsCleaning]: t(
      'vehicleCommon_vehicleInteraction_reason_frontCabinCleaning',
    ),
    [InteractionReason.SafetyDriverMatching]: t(
      'vehicleCommon_vehicleInteraction_reason_safetyDriverMatching',
    ),
  }[key]
}

/**
 * Maps a backend reason string to the InteractionReason enum.
 * Returns null if the string doesn't match any known enum value.
 */
export const mapReasonStringToInteractionReason = (
  reason: string | null,
): InteractionReason | null => {
  if (!reason) return null

  const enumValues = Object.values(InteractionReason) as string[]
  if (enumValues.includes(reason)) {
    return reason as InteractionReason
  }

  return null
}

export const useVehicleInteractionErrorDetail = () => {
  const { t } = useTranslation()

  return (interactionError: VehicleInteractionError) => {
    return {
      [VehicleInteractionError.AlreadyOpenedAnotherVehicle]: t(
        'vehicleCommon_vehicleInteraction_errorType_alreadyOpenedAnotherVehicle',
      ),
      [VehicleInteractionError.BoxProviderException]: t(
        'vehicleCommon_vehicleInteraction_errorType_boxProviderException',
      ),
      [VehicleInteractionError.CentralLockingTimeout]: t(
        'vehicleCommon_vehicleInteraction_errorType_centralLockingTimeout',
      ),
      [VehicleInteractionError.EmployeeIdMissing]: t(
        'vehicleCommon_vehicleInteraction_errorType_employeeIdMissing',
      ),
      [VehicleInteractionError.EmployeeSuspended]: t(
        'vehicleCommon_vehicleInteraction_errorType_employeeSuspended',
      ),
      [VehicleInteractionError.MissingPermission]: t(
        'vehicleCommon_vehicleInteraction_errorType_missingPermission',
      ),
      [VehicleInteractionError.NoBoxId]: t(
        'vehicleCommon_vehicleInteraction_errorType_noBoxId',
      ),
      [VehicleInteractionError.NoMatchingInteraction]: t(
        'vehicleCommon_vehicleInteraction_errorType_noMatchingInteraction',
      ),
      [VehicleInteractionError.PreconditionDoorOpen]: t(
        'vehicleCommon_vehicleInteraction_errorType_preconditionDoorOpen',
      ),
      [VehicleInteractionError.PreconditionFuelCardMissing]: t(
        'vehicleCommon_vehicleInteraction_errorType_preconditionFuelCardMissing',
      ),
      [VehicleInteractionError.PreconditionIgnitionOn]: t(
        'vehicleCommon_vehicleInteraction_errorType_preconditionIgnitionOn',
      ),
      [VehicleInteractionError.PreconditionKeyFob]: t(
        'vehicleCommon_vehicleInteraction_errorType_preconditionKeyFob',
      ),
      [VehicleInteractionError.PreconditionLightsOn]: t(
        'vehicleCommon_vehicleInteraction_errorType_preconditionLightsOn',
      ),
      [VehicleInteractionError.PreconditionWindowOpen]: t(
        'vehicleCommon_vehicleInteraction_errorType_preconditionWindowOpen',
      ),
      [VehicleInteractionError.ShiftIdMissing]: t(
        'vehicleCommon_vehicleInteraction_errorType_shiftIdMissing',
      ),
      [VehicleInteractionError.StateIncompatible]: t(
        'vehicleCommon_vehicleInteraction_errorType_stateIncompatible',
      ),
      [VehicleInteractionError.Unknown]: t(
        'vehicleCommon_vehicleInteraction_errorType_unknown',
      ),
      [VehicleInteractionError.VehicleAlreadyClosed]: t(
        'vehicleCommon_vehicleInteraction_errorType_vehicleAlreadyClosed',
      ),
      [VehicleInteractionError.VehicleAlreadyOpen]: t(
        'vehicleCommon_vehicleInteraction_errorType_vehicleAlreadyOpen',
      ),
      [VehicleInteractionError.VehicleClosedWithHardwareKey]: t(
        'vehicleCommon_vehicleInteraction_errorType_vehicleClosedWithHardwareKey',
      ),
      [VehicleInteractionError.VehicleOpenedWithHardwareKey]: t(
        'vehicleCommon_vehicleInteraction_errorType_vehicleOpenedWithHardwareKey',
      ),
      [VehicleInteractionError.VehicleInteractionInFlightConflict]: t(
        'vehicleCommon_vehicleInteraction_errorType_vehicleInteractionInFlightConflict',
      ),
      [VehicleInteractionError.VehicleNotFound]: t(
        'vehicleCommon_vehicleInteraction_errorType_vehicleNotFound',
      ),
      [VehicleInteractionError.VehicleReservedToOpen]: t(
        'vehicleCommon_vehicleInteraction_errorType_vehicleReservedToOpen',
      ),
      [VehicleInteractionError.VehicleTooFarAway]: t(
        'vehicleCommon_vehicleInteraction_errorType_vehicleTooFarAway',
      ),
      [VehicleInteractionError.WrongEmployee]: t(
        'vehicleCommon_vehicleInteraction_errorType_wrongEmployee',
      ),
    }[interactionError]
  }
}

export enum StateChangeReasonHeading {
  damages = 'DAMAGES',
  defects = 'DEFECTS',
  events = 'EVENTS',
  service = 'SERVICE',
}

export const useInactiveReasonHeadingTranslation = () => {
  const { t } = useTranslation()

  return (
    vehicleStateChangeReason: VehicleStateChangeReason | string | null,
  ): string => {
    if (!vehicleStateChangeReason)
      return t('vehicleCommon_stateChangeReasonHeading_empty')

    const reasonPrefix = vehicleStateChangeReason.split('_', 2)[0]
    switch (reasonPrefix) {
      case StateChangeReasonHeading.damages:
        return t('vehicleCommon_stateChangeReasonHeading_damages')
      case StateChangeReasonHeading.defects:
        return t('vehicleCommon_stateChangeReasonHeading_defects')
      case StateChangeReasonHeading.events:
        return t('vehicleCommon_stateChangeReasonHeading_events')
      case StateChangeReasonHeading.service:
        return t('vehicleCommon_stateChangeReasonHeading_service')
      default:
        return reasonPrefix
    }
  }
}

export const useInactiveReasonTranslation = () => {
  const { t } = useTranslation()

  return (
    vehicleStateChangeReason: VehicleStateChangeReason | null,
  ): string => {
    if (!vehicleStateChangeReason) return '-'

    const options = {
      [VehicleStateChangeReason.DamagesOther]: t(
        'vehicleCommon_vehicleStateChangeReason_damagesOther',
      ),
      [VehicleStateChangeReason.DamagesSelfRepairAtHub]: t(
        'vehicleCommon_vehicleStateChangeReason_damagesSelfRepairAtHub',
      ),
      [VehicleStateChangeReason.DamagesSmartRepairAtHub]: t(
        'vehicleCommon_vehicleStateChangeReason_damagesSmartRepairAtHub',
      ),
      [VehicleStateChangeReason.DamagesWorkshop]: t(
        'vehicleCommon_vehicleStateChangeReason_damagesWorkshop',
      ),
      [VehicleStateChangeReason.Defects_12V]: t(
        'vehicleCommon_vehicleStateChangeReason_defects12V',
      ),
      [VehicleStateChangeReason.DefectsErrorMessage]: t(
        'vehicleCommon_vehicleStateChangeReason_defectsErrorMessage',
      ),
      [VehicleStateChangeReason.DefectsHighVoltage]: t(
        'vehicleCommon_vehicleStateChangeReason_defectsHighVoltage',
      ),
      [VehicleStateChangeReason.DefectsHighVoltageBjb]: t(
        'vehicleCommon_vehicleStateChangeReason_defectsHighVoltageBjb',
      ),
      [VehicleStateChangeReason.DefectsIvc]: t(
        'vehicleCommon_vehicleStateChangeReason_defectsIvc',
      ),
      [VehicleStateChangeReason.DefectsOther]: t(
        'vehicleCommon_vehicleStateChangeReason_defectsOther',
      ),
      [VehicleStateChangeReason.EventsCleaning]: t(
        'vehicleCommon_vehicleStateChangeReason_eventsCleaning',
      ),
      [VehicleStateChangeReason.EventsDefleeting]: t(
        'vehicleCommon_vehicleStateChangeReason_eventsDefleeting',
      ),
      [VehicleStateChangeReason.EventsMarketing]: t(
        'vehicleCommon_vehicleStateChangeReason_eventsMarketing',
      ),
      [VehicleStateChangeReason.EventsOther]: t(
        'vehicleCommon_vehicleStateChangeReason_eventsOther',
      ),
      [VehicleStateChangeReason.EventsRental]: t(
        'vehicleCommon_vehicleStateChangeReason_eventsRental',
      ),
      [VehicleStateChangeReason.EventsServiceCancellation]: t(
        'vehicleCommon_vehicleStateChangeReason_eventsServiceCancellation',
      ),
      [VehicleStateChangeReason.EventsStickers]: t(
        'vehicleCommon_vehicleStateChangeReason_eventsStickers',
      ),
      [VehicleStateChangeReason.EventsUsableReserve]: t(
        'vehicleCommon_vehicleStateChangeReason_eventsUsableReserve',
      ),
      [VehicleStateChangeReason.ServiceMainInspection]: t(
        'vehicleCommon_vehicleStateChangeReason_serviceMainInspection',
      ),
      [VehicleStateChangeReason.ServiceMileageInspection]: t(
        'vehicleCommon_vehicleStateChangeReason_serviceMileageInspection',
      ),
      [VehicleStateChangeReason.ServiceMoiaVwAction]: t(
        'vehicleCommon_vehicleStateChangeReason_serviceMoiaVwAction',
      ),
      [VehicleStateChangeReason.ServiceOther]: t(
        'vehicleCommon_vehicleStateChangeReason_serviceOther',
      ),
      [VehicleStateChangeReason.ServiceTireCheckSwap]: t(
        'vehicleCommon_vehicleStateChangeReason_serviceTireCheckSwap',
      ),
      [VehicleStateChangeReason.ServiceVic]: t(
        'vehicleCommon_vehicleStateChangeReason_serviceVic',
      ),
    }

    return options[vehicleStateChangeReason] ?? vehicleStateChangeReason
  }
}

export const useOperationalStateChangeReasonTranslation = () => {
  const { t } = useTranslation()

  return (
    reason: VehicleNonOperationalReasonType | null | undefined,
  ): string => {
    if (!reason) return '-'

    const options = {
      [VehicleNonOperationalReasonType.PreRideCheckExpired]: t(
        'vehicleCommon_operationalStateChangeReason_preRideCheckExpired',
      ),
      [VehicleNonOperationalReasonType.PreRideCheckInvalid]: t(
        'vehicleCommon_operationalStateChangeReason_preRideCheckInvalid',
      ),
      [VehicleNonOperationalReasonType.PreRideCheckPending]: t(
        'vehicleCommon_operationalStateChangeReason_preRideCheckPending',
      ),
      [VehicleNonOperationalReasonType.WorkOrder]: t(
        'vehicleCommon_operationalStateChangeReason_workOrder',
      ),
      [VehicleNonOperationalReasonType.WorkOrderOngoing]: t(
        'vehicleCommon_operationalStateChangeReason_workOrder',
      ),
      [VehicleNonOperationalReasonType.VehicleDeactivated]: t(
        'vehicleCommon_operationalStateChangeReason_vehicleDeactivated',
      ),
    }

    return options[reason] ?? reason
  }
}
